import { FileDownloadOutlined } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import {
  App,
  Plugin,
  Route,
  UniversalAppState,
  View,
} from "@pimo/pimo-app-builder";
import {
  ChipCell,
  DateCell,
  DefaultOverlayPartialAppState,
  DragDropLayoutTabDefinition,
  generateTableHeaderEntriesWithSorting,
  PMButton,
  PMTableSortEventPayload,
  ProgressBarCell,
  SortDirection,
  StatusIndicatorCell,
  TableUpdateStatusCell,
  TextCardCell,
} from "@pimo/pimo-components";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import { PMSmallNumber } from "@pimo/pimo-components/src/lib/pm-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { Project } from "product-test-types";
import {
  canUserPerformOperation,
  getContrastColor,
  getCustomFieldAggregationComponentId,
  getCustomFieldPublicId,
  getCustomFieldTableColumnFieldName,
} from "product-test-utils";
import React from "react";
import { generatePath } from "react-router-dom";

import { TestAppState } from "../app";
import { APP_ROUTES } from "../constants";
import { buildManagementSummaryFormRoute } from "../form/build-management-summary-form-route";
import { downloadPdf, getRequestedPdfTabIndex } from "../helpers/download-pdf";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import {
  addProjectsTableComponent,
  type ProjectsTableColumnProps,
} from "../helpers/pages-helpers/projects-table-component";
import { showSnackbar } from "../helpers/show-snackbar";
import {
  getDropdownColorValue,
  getDropdownLabel,
} from "../utils/dropdown-options";
import { formatDate } from "../utils/format-date";
import { getLocalizedTableColumnLabel } from "../utils/get-localized-table-column-label";
import { sortProjects } from "../utils/sort-table-data";
import { DASHBOARD_CARDS } from "./dashboard";
import { createDashboardCardContext } from "./dashboard/context";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DashboardPluginState {}

export class DashboardPlugin implements Plugin<TestAppState> {
  private view?: View<TestAppState, DragAndDropLayout>;
  route?: Route;

  private app?: App<TestAppState>;
  private downloadMenuAnchor?: HTMLElement | null;
  onRegister(app: App<TestAppState>) {
    this.app = app;
    this.view = app.createView({
      name: "dashboard",
      layout: new DragAndDropLayout(),
    });
    this.view.mapLayoutState(
      ({ dashboardLayout, userProfile, language, settings }) => {
        let tabs;
        if (dashboardLayout?.tabs && dashboardLayout.tabs.length > 0) {
          tabs = dashboardLayout.tabs;
        } else {
          tabs = [
            {
              id: 0,
              title: getLocalizedMessage(language, "dashboard"),
              rows: [
                {
                  id: 0,
                  columnCount: 1,
                  columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                },
              ],
            },
          ];
        }

        const canEditManagementSummary = canUserPerformOperation({
          userGroups: userProfile?.groups || [],
          lowestLevelAccessRequiredToPerformThisOperation: "program",
          client: settings?.configuration?.client,
          tool: settings?.configuration?.tool,
        });

        const isManagementSummaryOnDashboard = tabs.some(
          (tab: DragDropLayoutTabDefinition) =>
            tab.rows?.some((row) =>
              row.columns?.some(
                (col) => col.componentId === "managementSummaryComponent"
              )
            )
        );
        const downloadTriggerTestId = {
          "data-testid": "dashboard-download-trigger",
        } as const;

        return {
          canEdit: canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          }),
          isComponentEditingEnabled: true,
          displayTabs: dashboardLayout?.displayTabs ?? true,
          activeTabIndex: getRequestedPdfTabIndex(),
          language: language || "en",
          tabs,
          rightSlot: React.createElement(
            Box,
            {
              className: "no-print",
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
            canEditManagementSummary && isManagementSummaryOnDashboard
              ? React.createElement(PMButton, {
                  size: "medium",
                  variant: "report",
                  text: "Update Management Summary",
                  onClick: () => {
                    this.app?.navigate(APP_ROUTES.editManagementSummary);
                  },
                })
              : null,
            React.createElement(
              IconButton,
              {
                ...downloadTriggerTestId,
                // Give the download button an accessible name so tests can reliably open it.
                "aria-label":
                  getLocalizedMessage(language, "download.pdf") ||
                  "Download PDF",
                onClick: (event: React.MouseEvent<HTMLElement>) => {
                  this.downloadMenuAnchor = event.currentTarget;
                  this.app?.patchAppState({});
                },
                sx: { ml: 1 },
              },
              React.createElement(FileDownloadOutlined)
            ),
            React.createElement(
              Menu,
              {
                anchorEl: this.downloadMenuAnchor,
                open: Boolean(this.downloadMenuAnchor),
                onClose: () => {
                  this.downloadMenuAnchor = null;
                  this.app?.patchAppState({});
                },
              },
              React.createElement(
                MenuItem,
                {
                  onClick: () => {
                    this.downloadMenuAnchor = null;
                    this.app?.patchAppState({});
                    void downloadPdf().catch((error: unknown) => {
                      console.error("PDF export failed", error);
                      if (this.app) {
                        showSnackbar(
                          this.app as unknown as App<
                            UniversalAppState & DefaultOverlayPartialAppState
                          >,
                          getLocalizedMessage(
                            language,
                            "generic.internalError"
                          ) || "Could not generate the PDF.",
                          "error"
                        );
                      }
                    });
                  },
                },
                getLocalizedMessage(language, "download.pdf") || "Download PDF"
              )
            )
          ),
        };
      }
    );
    this.view.on("componentEdited", async ({ payload }) => {
      if (!payload) {
        return;
      }

      const componentEditedPayload = payload as {
        componentId: string;
        componentValue: Record<string, unknown>;
      };

      if (
        !componentEditedPayload.componentId ||
        !componentEditedPayload.componentValue
      ) {
        return;
      }

      const { componentId, componentValue } = componentEditedPayload;
      this.app?.patchAppState({
        dashboardComponentValues: {
          ...this.app?.getAppState()?.dashboardComponentValues,
          [componentId]: componentValue,
        },
      });
    });
    this.buildDashboardView();
    this.route = app.createRoute({
      path: APP_ROUTES.dashboard,
      view: this.view,
    });

    buildManagementSummaryFormRoute(app, this.route);

    this.view.on("updateLayout", async ({ payload }) => {
      if (!payload || !this.app) return;

      const { dashboardLayout } = this.app.getAppState();

      const currentDisplayTabs = dashboardLayout?.displayTabs ?? true;

      const { tabs } = payload as { tabs: DragDropLayoutTabDefinition[] };

      this.app.patchAppState({
        dashboardLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      await createViewConfiguration({
        tabs,
        canEdit: dashboardLayout?.canEdit ?? true,
        displayTabs: currentDisplayTabs,
        type: "dashboard",
      });
    });

    this.view?.on("startLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: false,
      });
    });

    this.view?.on("stopLayoutEdit", () => {
      this.app?.patchAppState({
        isSidebarOpen: true,
      });
    });

    this.view.buildDynamicallyBasedOnAppState(
      (appState) => ({
        configuration: appState.settings?.configuration,
        dashboard: appState.dashboard,
        language: appState.language,
      }),

      (state, view) => {
        // static components are built here
        this.buildDashboardView();

        const { projectsTableComponent } = addProjectsTableComponent({
          view,
          configuration: state.configuration,
        });

        projectsTableComponent.on("sort", (event) => {
          if (!event?.payload || !this.app) {
            return;
          }

          const payload = event.payload as PMTableSortEventPayload<
            keyof Project
          >;
          const appState = this.app.getAppState();
          let direction: SortDirection = "ASC";

          if (
            appState.overviewPluginSortData?.key === payload?.sort?.key &&
            appState.overviewPluginSortData?.direction === "ASC"
          ) {
            direction = "DESC";
          }

          this.app.patchAppState({
            overviewPluginSortData: {
              key: payload?.sort?.key,
              direction,
            },
          });
        });

        projectsTableComponent.mapState(
          ({ dashboard, settings, overviewPluginSortData, language }) => {
            const projectItems = sortProjects(
              dashboard?.projects ?? [],
              overviewPluginSortData
            );

            const customFieldDefinitions =
              state.configuration?.customFields?.filter(
                (field) =>
                  field?.enabled &&
                  field?.flag !== "delete" &&
                  (field?.purpose === "projectBasic" ||
                    field?.purpose === "projectReporting")
              ) ?? [];

            const baseHeaders = generateTableHeaderEntriesWithSorting<
              Project & {
                progress: number;
                status: string;
                degreeOfCompletion: string;
                updateStatus: string;
              }
            >({
              entries: [
                "customId",
                "name",
                "owner",
                "category",
                "status",
                "degreeOfCompletion",
                "progress",
                "startDate",
                "endDate",
                "updateStatus",
              ].map((fieldName) => ({
                fieldName: fieldName as keyof (Project & {
                  progress: number;
                  status: string;
                  degreeOfCompletion: string;
                  updateStatus: string;
                }),
                label: getLocalizedTableColumnLabel(language, fieldName),
                isSortable: true,
                isHidden: false,
                sortDirection: null,
              })),
              tableSortData: overviewPluginSortData,
            });

            const customHeaders = customFieldDefinitions.map((field) => ({
              fieldName: getCustomFieldTableColumnFieldName(field),
              label: field.label,
              isSortable: true,
              isHidden: false,
              sortDirection: null,
            }));

            const tableHeaderEntries = [...baseHeaders, ...customHeaders];

            const data = projectItems.map((entry) => {
              const statusName =
                settings?.configuration?.statusReportingLevel === "category"
                  ? (entry?._calculated?.overallSemaphoreStatus?.name ?? "")
                  : (entry?._calculated?.overallStatus?.name ?? "");

              const statusColor =
                settings?.configuration?.statusReportingLevel === "category"
                  ? (entry?._calculated?.overallSemaphoreStatus?.color ?? "")
                  : getDropdownColorValue(
                      settings?.configuration?.statusDefinition,
                      entry?._calculated?.overallStatus?.name,
                      ""
                    );

              const categoryColor = getDropdownColorValue(
                settings?.configuration?.categoryDefinition,
                entry.category,
                "#0B2753"
              );

              const baseColumnProps = [
                { component: TextCardCell, body: String(entry.customId ?? "") },
                { component: TextCardCell, body: String(entry.name ?? "") },
                { component: TextCardCell, body: String(entry.owner ?? "") },
                {
                  component: ChipCell,
                  body: getDropdownLabel(
                    settings?.configuration?.categoryDefinition,
                    entry.category
                  ),
                  chipProps: {
                    sx: {
                      backgroundColor: categoryColor,
                      color: getContrastColor(categoryColor, "#ffffff"),
                      borderRadius: "8px",
                      px: 2,
                    },
                  },
                },
                {
                  component: StatusIndicatorCell,
                  body: statusName,
                  status: statusColor,
                },
                {
                  component: StatusIndicatorCell,
                  body:
                    entry?._calculated?.overallDegreeOfCompletion?.name ?? "",
                  status: getDropdownColorValue(
                    settings?.configuration?.degreeOfCompletionDefinition,
                    entry?._calculated?.overallDegreeOfCompletion?.name,
                    ""
                  ),
                },
                {
                  component: ProgressBarCell,
                  header: `${Math.round(entry?._calculated?.averageProgress ?? 0)} %`,
                  progress: entry?._calculated?.averageProgress ?? 0,
                },
                {
                  component: DateCell,
                  date: entry?.startDate ? formatDate(entry.startDate) : "N/A",
                },
                {
                  component: DateCell,
                  date: entry?.endDate ? formatDate(entry.endDate) : "N/A",
                },
                {
                  component: TableUpdateStatusCell,
                  body: entry._calculated?.updateStatus?.status ?? "",
                  icon: entry._calculated?.updateStatus?.icon,
                  chipColor: entry._calculated?.updateStatus?.color,
                  chipProps: {
                    sx: {
                      color: entry._calculated?.updateStatus?.contrastTextColor,
                    },
                  },
                },
              ];

              const customFieldValueByFieldName = new Map<string, unknown>();
              for (const customFieldValue of entry.customFields ?? []) {
                customFieldValueByFieldName.set(
                  getCustomFieldTableColumnFieldName(
                    customFieldValue.customFieldDefinition
                  ),
                  customFieldValue.value
                );
              }

              const customColumnProps = customHeaders.map((header) => {
                const fieldName = String(header.fieldName);
                const customDefinition = customFieldDefinitions.find(
                  (field) =>
                    getCustomFieldTableColumnFieldName(field) === fieldName
                );
                const rawValue = customFieldValueByFieldName.get(fieldName);

                if (customDefinition?.type === "Date") {
                  return {
                    component: DateCell,
                    date: rawValue ? formatDate(String(rawValue)) : "N/A",
                  };
                }

                if (customDefinition?.type === "Select") {
                  const optionColor = getDropdownColorValue(
                    customDefinition.options,
                    String(rawValue ?? ""),
                    "#E1F1E7"
                  );

                  return {
                    component: ChipCell,
                    body: getDropdownLabel(
                      customDefinition.options,
                      String(rawValue ?? "")
                    ),
                    chipProps: {
                      sx: {
                        backgroundColor: optionColor,
                        color: getContrastColor(optionColor, "#ffffff"),
                        borderRadius: "8px",
                        px: 2,
                      },
                    },
                  };
                }

                return {
                  component: TextCardCell,
                  body: String(rawValue ?? "n/a"),
                };
              });

              return {
                columnProps: [
                  ...baseColumnProps,
                  ...customColumnProps,
                ] as unknown as ProjectsTableColumnProps,
                rowProps: {
                  cardProps: { sx: { cursor: "pointer" } },
                  onClick: () => {
                    this.app?.navigate(
                      generatePath(APP_ROUTES.report, {
                        id: entry.id,
                        viewname: "dashboard",
                      })
                    );
                  },
                  lockMenu: false,
                },
              };
            });

            return {
              data,
              tableHeaderEntries,
              language,
              showInfoIcon: false,
              showMenuIcon: false,
            };
          }
        );

        for (const [recordId, sumValue] of Object.entries(
          state.dashboard?._calculated?.itemsCustomFieldSums ?? {}
        )) {
          const matchingCustomFieldDefinition =
            state.configuration?.customFields?.find(
              (customField) =>
                getCustomFieldPublicId(customField) === String(recordId)
            );

          if (!matchingCustomFieldDefinition) {
            continue;
          }

          const customFieldSumTextCard = view.addComponent({
            component: PMSmallNumber,
            layoutProps: {
              componentId: getCustomFieldAggregationComponentId(
                "dashboard-item-sum",
                matchingCustomFieldDefinition
              ),
              labels: [matchingCustomFieldDefinition.label, "customFieldSums"],
              description: `Overall sum for ${matchingCustomFieldDefinition.label}`,
              title: `Overall sum for ${matchingCustomFieldDefinition.label}`,
            },
          });

          customFieldSumTextCard.mapState(() => ({
            showInfoIcon: false,
            showMenuIcon: false,
            title: `Sum of ${matchingCustomFieldDefinition.label}`,
            description: `Overall sum for ${matchingCustomFieldDefinition.label}`,
            number: Number(sumValue) ?? 0,
          }));
        }

        for (const [recordId, averageValue] of Object.entries(
          state.dashboard?._calculated?.itemsCustomFieldAverages ?? {}
        )) {
          const matchingCustomFieldDefinition =
            state.configuration?.customFields?.find(
              (customField) =>
                getCustomFieldPublicId(customField) === String(recordId)
            );

          if (!matchingCustomFieldDefinition) {
            continue;
          }

          const customFieldAverageTextCard = view.addComponent({
            component: PMSmallNumber,
            layoutProps: {
              componentId: getCustomFieldAggregationComponentId(
                "dashboard-item-average",
                matchingCustomFieldDefinition
              ),
              labels: [
                matchingCustomFieldDefinition.label,
                "customFieldAverages",
              ],
              description: `Overall average for ${matchingCustomFieldDefinition.label}`,
              title: `Overall average for ${matchingCustomFieldDefinition.label}`,
            },
          });

          customFieldAverageTextCard.mapState(() => ({
            showInfoIcon: false,
            showMenuIcon: false,
            title: `Average of ${matchingCustomFieldDefinition.label}`,
            description: `Overall average for ${matchingCustomFieldDefinition.label}`,
            number: Number(averageValue) ?? 0,
            roundToDecimals: 2,
          }));
        }

        for (const [recordId, sumValue] of Object.entries(
          state.dashboard?._calculated?.projectsCustomFieldSums ?? {}
        )) {
          const matchingCustomFieldDefinition =
            state.configuration?.customFields?.find(
              (customField) =>
                getCustomFieldPublicId(customField) === String(recordId)
            );

          if (!matchingCustomFieldDefinition) {
            continue;
          }

          const customFieldSumTextCard = view.addComponent({
            component: PMSmallNumber,
            layoutProps: {
              componentId: getCustomFieldAggregationComponentId(
                "dashboard-project-sum",
                matchingCustomFieldDefinition
              ),
              labels: [matchingCustomFieldDefinition.label, "customFieldSums"],
              title: `Overall sum for ${matchingCustomFieldDefinition.label}`,
              description: `Overall sum for ${matchingCustomFieldDefinition.label}`,
            },
          });

          customFieldSumTextCard.mapState(() => ({
            showInfoIcon: false,
            showMenuIcon: false,
            title: `Sum of ${matchingCustomFieldDefinition.label}`,
            description: `Overall sum for ${matchingCustomFieldDefinition.label}`,
            number: Number(sumValue) ?? 0,
          }));
        }

        for (const [recordId, averageValue] of Object.entries(
          state.dashboard?._calculated?.projectsCustomFieldAverages ?? {}
        )) {
          const matchingCustomFieldDefinition =
            state.configuration?.customFields?.find(
              (customField) =>
                getCustomFieldPublicId(customField) === String(recordId)
            );

          if (!matchingCustomFieldDefinition) {
            continue;
          }

          const customFieldAverageTextCard = view.addComponent({
            component: PMSmallNumber,
            layoutProps: {
              componentId: getCustomFieldAggregationComponentId(
                "dashboard-project-average",
                matchingCustomFieldDefinition
              ),
              labels: [
                matchingCustomFieldDefinition.label,
                "customFieldAverages",
              ],
              description: `Overall average for ${matchingCustomFieldDefinition.label}`,
              title: `Overall average for ${matchingCustomFieldDefinition.label}`,
            },
          });

          customFieldAverageTextCard.mapState(() => ({
            showInfoIcon: false,
            showMenuIcon: false,
            title: `Average of ${matchingCustomFieldDefinition.label}`,
            description: `Overall average for ${matchingCustomFieldDefinition.label}`,
            number: Number(averageValue) ?? 0,
            roundToDecimals: 2,
          }));
        }
      }
    );
  }

  buildDashboardView() {
    if (!this.app || !this.view) {
      return;
    }

    // One call per card, in layout order. Each lives in ./dashboard/cards and
    // owns its own `addComponent` and `mapState`; none of them reads anything
    // the others define, which is why they could be lifted out one for one.
    // The order lives in DASHBOARD_CARDS, beside the cards themselves, so a
    // spec can assert it — see dashboard/dashboard.structure.spec.ts.
    const ctx = createDashboardCardContext(this.view, this.app);
    for (const addCard of DASHBOARD_CARDS) {
      addCard(ctx);
    }
  }
}
